import * as Dialog from '@radix-ui/react-dialog';
import { animated, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import { isUndefined } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef } from 'react';

type AnimationConfig = Parameters<typeof useTransition>[1];

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  label?: string;
  animation: AnimationConfig;
  dismissable: boolean;
  preventAutofocusOnOpen?: boolean;
  zIndexIncrease?: 10 | 20;
};

export const BaseModal = ({ children, isOpen, onClose, className, label, animation, dismissable, preventAutofocusOnOpen = false, zIndexIncrease }: PropsWithChildren<ModalProps>) => {
  const modalTransitions = useTransition(isOpen, animation);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  }, [onClose]);

  const handleDismiss = useCallback((event: Event) => {
    if (!dismissable) {
      event.preventDefault();
    }
  }, [dismissable]);

  const modalBackgroundRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      const modal = modalRef.current;
      const modalBackground = modalBackgroundRef.current;

      if (isOpen) {
        modal?.classList.add('opacity-100');
        modal?.classList.remove('opacity-0');
        modalBackground?.classList.add('opacity-100');
        modalBackground?.classList.remove('opacity-0');
      } else {
        modal?.classList.add('opacity-0');
        modal?.classList.remove('opacity-100');
        modalBackground?.classList.add('opacity-0');
        modalBackground?.classList.remove('opacity-100');
      }
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [isOpen]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      {modalTransitions((modalStyles, modalVisible) => modalVisible ? (
        <Dialog.Portal forceMount>

          <Dialog.Overlay forceMount asChild>
            <div ref={modalBackgroundRef} className={classNames('fixed inset-0 opacity-0 transition-opacity duration-300 tds-modal-background', {
              'z-[40]': isUndefined(zIndexIncrease),
              'z-[50]': zIndexIncrease === 10,
              'z-[60]': zIndexIncrease === 20,
            })} />
          </Dialog.Overlay>

          {modalVisible ? (
            <Dialog.Content onOpenAutoFocus={preventAutofocusOnOpen ? event => event.preventDefault() : undefined} forceMount asChild onPointerDownOutside={handleDismiss} onEscapeKeyDown={handleDismiss} aria-describedby={undefined}>
              <animated.div ref={modalRef} className={classNames('fixed opacity-0 transition-opacity duration-300', {
                'z-[50]': isUndefined(zIndexIncrease),
                'z-[60]': zIndexIncrease === 10,
                'z-[70]': zIndexIncrease === 20,
              }, className)} style={modalStyles} aria-label={label}>
                {children}
              </animated.div>
            </Dialog.Content>
          ) : null}

        </Dialog.Portal>
      ) : null)}

    </Dialog.Root>
  );
};
